import * as React from 'react';
import {useSelector} from 'react-redux';

import {ConditionalSelectField} from 'shells/conditional-select-field/conditional-select-field';
import {AccountIcon} from 'shells/icon/svg/account-icon';
import {ContactIcon} from 'shells/icon/svg/contact-icon';
import {LeadIcon} from 'shells/icon/svg/lead-icon';
import type {NoOptionsMessageProps} from 'shells/select-picker-generic/select-picker-generic';

import {getFormSchema} from '../../forms-selectors';

import {useGetDecisionFields} from './graphql/hooks';
import type {EntityType} from './graphql/hooks';

type Props = {
    formId: string;
    entityType?: EntityType;
    fieldCondition: {
        title: string;
        placeholder?: string;
        default?: {
            fieldId: string;
            attributeId: string;
            label: string;
        };
        isDisabled?: boolean;
        tooltip?: string;
    };
    optionCondition: {
        title: string;
        placeholder?: string;
    };
    customNoOptionsMessage?: NoOptionsMessageProps;
    onUpdate: (fieldId: string, optionId?: string | null | undefined) => void;
};

export function DecisionFieldRule(props: Props) {
    const [selectedFieldId, setSelectedFieldId] = React.useState(
        props.fieldCondition.default && props.fieldCondition.default.fieldId
    );
    const [selectedFieldAttributeId, setSelectedFieldAttributeId] = React.useState(
        props.fieldCondition.default && props.fieldCondition.default.attributeId
    );
    const [selectedOptionId, setSelectedOptionId] = React.useState<any>(null);

    const formSchema = useSelector(getFormSchema);
    const selectConditionalField = formSchema.properties[selectedFieldAttributeId];

    const {fields} = useGetDecisionFields(props.formId, props.entityType);

    const conditionalOptions = [];
    for (const field of fields) {
        conditionalOptions.push({
            label: field.label,
            value: getConditionalOption(field.attribute.id, field.id),
            icon: field.entityName ? getIconForField(field.entityName) : undefined,
        });
    }

    const consequentOptions = [];
    if (selectConditionalField && selectConditionalField.options) {
        for (const option of selectConditionalField.options) {
            consequentOptions.push({
                label: option.name,
                value: option.id,
            });
        }
    }

    return (
        <ConditionalSelectField
            conditional={{
                title: props.fieldCondition.title,
                placeholder: props.fieldCondition.placeholder,
                options: conditionalOptions,
                selectedOption:
                    selectedFieldAttributeId && selectedFieldId
                        ? getConditionalOption(selectedFieldAttributeId, selectedFieldId)
                        : null,
                onChange: (option) => {
                    if (option.includes('_')) {
                        const [fieldAttributeId, fieldId] = option.split('_');
                        setSelectedFieldAttributeId(fieldAttributeId);
                        setSelectedFieldId(fieldId);
                        setSelectedOptionId(null);
                        // @ts-expect-error
                        props.onUpdate(fieldId, null);
                    }
                },
                customNoOptionsMessage: props.customNoOptionsMessage,
                isDisabled: props.fieldCondition.isDisabled,
                tooltip: props.fieldCondition.tooltip,
            }}
            consequent={{
                title: props.optionCondition.title,
                placeholder: props.optionCondition.placeholder,
                options: consequentOptions,
                selectedOption: selectedOptionId,
                onChange: (option) => {
                    setSelectedOptionId(option);
                    if (selectedFieldId) {
                        props.onUpdate(selectedFieldId, option);
                    }
                },
            }}
        />
    );
}

// The value needs to be in the format of `attributeId_fieldId`. The attributeId allows us to grab the options from Redux
// while the fieldId is used by users of this component to update the rule.
function getConditionalOption(attributeId: string, fieldId: string): string {
    return `${attributeId}_${fieldId}`;
}

function getIconForField(entityName: string): React.ComponentType<any> | null | undefined {
    switch (entityName.toLowerCase()) {
        case 'lead':
            return LeadIcon;
        case 'account':
            return AccountIcon;
        case 'contact':
            return ContactIcon;
        default:
            return null;
    }
}
